import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Sparkles, Shuffle, Coffee, Smile, BookOpen, Heart, Lightbulb, Flame, ChevronRight, Trophy } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess, getPartnerId, getDisplayName } from '../../lib/owner'
import { findQuestion, scoreRound, ROUND_SIZE } from '../../components/games/quiz/logic'
import { useQuizRounds } from '../../components/games/quiz/hooks'
import { useQuestionPool } from '../../components/games/questions/hooks'
import { RevealDialog } from './QuizPage'
import { cn } from '../../lib/utils'

// Local copy of the deck icon map. Kept in sync with QuizPage.jsx;
// duplicated here so the history list doesn't pull the whole quiz UI
// surface in just for icon lookups.
const DECK_ICONS = {
  preferences: Coffee,
  dreams:      Sparkles,
  goofy:       Smile,
  memories:    BookOpen,
  spicy:       Heart,
  deep:        Lightbulb,
  intimate:    Flame,
}

// Completed-quizzes history. Lists every round where both partners
// finished, newest first, with deck + date + the symmetric score.
// Tap a row → opens the same RevealDialog the live reveal uses, so
// the per-question review reads identically to when the round just
// landed.
//
// Data comes from the same useQuizRounds hook the main page uses —
// it already includes "recently closed" rounds (72h cutoff). For a
// deeper history we'd need a separate paginated query against
// quiz_rounds where completed_at IS NOT NULL; deferring until the
// trusted pair piles up enough rounds for that to matter.
export function QuizHistoryPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const partnerId = useMemo(() => getPartnerId(user), [user])
  const partnerName = useMemo(() => getDisplayName(partnerId, locale), [partnerId, locale])

  const { rounds, answersByRound, refetch } = useQuizRounds(user?.id, partnerId)
  const { pool } = useQuestionPool(user?.id)

  // Closed rounds where both sides actually finished — i.e. real
  // completions, not legacy abandoned rounds (those have
  // completed_at set but missing answers).
  const completed = useMemo(() => {
    if (!user?.id || !partnerId) return []
    const list = rounds
      .filter((r) => !!r.completed_at)
      .map((r) => {
        const ans = answersByRound.get(r.id) || []
        const myAns = ans.filter((a) => a.user_id === user.id)
        const theirAns = ans.filter((a) => a.user_id === partnerId)
        return {
          ...r,
          answers: ans,
          bothComplete: myAns.length >= ROUND_SIZE && theirAns.length >= ROUND_SIZE,
        }
      })
      .filter((r) => r.bothComplete)
    list.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
    return list
  }, [rounds, answersByRound, user?.id, partnerId])

  const [revealOpen, setRevealOpen] = useState(null)
  const localText = useCallback((obj) => (obj?.[locale] || obj?.en || ''), [locale])
  const handleRefresh = async () => { await refetch() }

  if (!user) return null

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate('/games/quiz')}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
            {t('games.quiz.history.title')}
          </h1>
        </div>

        {completed.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
              {t('games.quiz.history.empty')}
            </CardContent>
          </Card>
        )}

        <div className="space-y-2">
          {completed.map((round) => (
            <HistoryRow
              key={round.id}
              t={t}
              round={round}
              userId={user.id}
              partnerId={partnerId}
              locale={locale}
              onClick={() => setRevealOpen(round)}
            />
          ))}
        </div>
      </div>

      {revealOpen && (
        <RevealDialog
          t={t}
          user={user}
          round={revealOpen}
          partnerName={partnerName}
          partnerId={partnerId}
          localText={localText}
          pool={pool}
          onClose={() => setRevealOpen(null)}
          onStartAnother={() => {
            setRevealOpen(null)
            navigate('/games/quiz')
          }}
        />
      )}
    </PullToRefresh>
  )
}

function HistoryRow({ t, round, userId, partnerId, locale, onClick }) {
  const Icon = round.category ? (DECK_ICONS[round.category] || Sparkles) : Shuffle
  const deckLabel = round.category
    ? t(`games.quiz.deck.${round.category}.name`)
    : t('games.quiz.deck.surprise.name')
  const dateLabel = useMemo(() => {
    try {
      return new Date(round.completed_at).toLocaleDateString(
        locale === 'ar' ? 'ar-EG' : 'en-US',
        { day: 'numeric', month: 'short', year: 'numeric' },
      )
    } catch { return '' }
  }, [round.completed_at, locale])

  const mine = scoreRound(round.answers, round.question_ids, userId, partnerId)
  const theirs = scoreRound(round.answers, round.question_ids, partnerId, userId)

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-border bg-card hover:bg-accent transition-colors text-start"
      dir="auto"
    >
      <span className="p-2 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground" dir="auto">{deckLabel}</p>
        <p className="text-[11px] text-muted-foreground" dir="auto">{dateLabel}</p>
      </div>
      <div className="flex items-center gap-1.5 text-xs font-mono tabular-nums text-app-primary-500 flex-shrink-0" dir="ltr">
        <Trophy className="w-3.5 h-3.5" />
        <span>{mine ? mine.correct : '—'}/{theirs ? theirs.correct : '—'}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
    </button>
  )
}

export default QuizHistoryPage
